import { convertBedrockBlock } from './bedrockBlocks';

/** Education's creative inventory, in the order the game shows it. */
export type InventoryCategory = 'construction' | 'nature' | 'equipment' | 'items';
/** A tile on a creative tab: a collapsible group, or a single item when `group` is absent. */
export interface InventoryTile { group?: string; icon: string; items: string[] }
export type InventoryLayout = Record<InventoryCategory, InventoryTile[]>;

/** Tabs from left to right in the creative inventory. */
export const TAB_ORDER: InventoryCategory[] = ['construction', 'equipment', 'items', 'nature'];

export interface InventoryLocation {
  category: InventoryCategory;
  /** Index in TAB_ORDER. */
  tabIndex: number;
  tileIndex: number;
  tile: InventoryTile;
  /** Position inside the group, or null for an ungrouped tile. */
  itemIndex: number | null;
  /** Catalog item ID, e.g. `minecraft:bed:14`. */
  item: string;
}

function withoutNamespace(name: string): string {
  return name.startsWith('minecraft:') ? name.slice('minecraft:'.length) : name;
}

const BED_COLORS = ['white', 'orange', 'magenta', 'light_blue', 'yellow', 'lime', 'pink', 'gray', 'light_gray', 'cyan', 'purple', 'blue', 'brown', 'green', 'red', 'black'];

const DIRECT_ITEM_NAMES: Record<string, string> = {
  wall_torch: 'torch',
  soul_wall_torch: 'soul_torch',
  redstone_wall_torch: 'redstone_torch',
  redstone_wire: 'redstone',
  tripwire: 'string',
  water: 'water_bucket',
  lava: 'lava_bucket',
  carrots: 'carrot',
  potatoes: 'potato',
  beetroots: 'beetroot_seeds',
  wheat: 'wheat_seeds',
  chain: 'iron_chain',
};

/** Placed shapes whose creative item has a different ID. */
function handMappedId(name: string): string | null {
  const bedColor = name.endsWith('_bed') ? BED_COLORS.indexOf(name.slice(0, -'_bed'.length)) : -1;
  if (bedColor >= 0) return bedColor === 0 ? 'minecraft:bed' : `minecraft:bed:${bedColor}`;
  if (Object.hasOwn(DIRECT_ITEM_NAMES, name)) return `minecraft:${DIRECT_ITEM_NAMES[name]}`;
  if (name.startsWith('potted_')) return 'minecraft:flower_pot';
  const hangingSign = /^(.+)_wall_hanging_sign$/.exec(name);
  if (hangingSign) return `minecraft:${hangingSign[1]}_hanging_sign`;
  const wallSign = /^(.+)_wall_sign$/.exec(name);
  if (wallSign) return `minecraft:${wallSign[1]}_sign`;
  return null;
}

interface LayoutIndex {
  byId: Map<string, InventoryLocation>;
  reverse: Map<string, string>;
}

const indexes = new WeakMap<InventoryLayout, LayoutIndex>();

function indexLayout(layout: InventoryLayout): LayoutIndex {
  let index = indexes.get(layout);
  if (index) return index;
  const byId = new Map<string, InventoryLocation>();
  const reverse = new Map<string, string>();
  const entries: InventoryTile[] = [];
  for (const [category, tiles] of Object.entries(layout) as [InventoryCategory, InventoryTile[]][]) {
    tiles.forEach((tile, tileIndex) => {
      entries.push(tile);
      tile.items.forEach((item, itemIndex) => {
        const location: InventoryLocation = {
          category,
          tabIndex: TAB_ORDER.indexOf(category),
          tileIndex,
          tile,
          itemIndex: tile.group ? itemIndex : null,
          item,
        };
        if (!byId.has(item)) byId.set(item, location);
        const base = item.replace(/:\d+$/, '');
        const javaName = withoutNamespace(convertBedrockBlock(base, {}).name);
        if (!reverse.has(javaName)) reverse.set(javaName, item);
      });
    });
  }
  // Raw catalog IDs are a fallback only; Java names keep their own meaning when
  // the same string exists in both vocabularies (e.g. stone_stairs, snow).
  for (const tile of entries) {
    for (const item of tile.items) {
      const itemName = withoutNamespace(item);
      if (!reverse.has(itemName)) reverse.set(itemName, item);
    }
  }
  index = { byId, reverse };
  indexes.set(layout, index);
  return index;
}

/** Where a placed block (Java-style name) or catalog item ID sits in the creative inventory. */
export function inventoryLocation(layout: InventoryLayout, name: string): InventoryLocation | null {
  const { byId, reverse } = indexLayout(layout);
  if (/:\d+$/.test(name)) {
    const numbered = byId.get(name) ?? byId.get(`minecraft:${name}`);
    if (numbered) return numbered;
  }
  if (name.includes(':') && !name.startsWith('minecraft:')) return null;
  const key = withoutNamespace(name);
  const handMapped = handMappedId(key);
  if (handMapped && byId.has(handMapped)) return byId.get(handMapped)!;
  const mapped = reverse.get(key);
  if (mapped && byId.has(mapped)) return byId.get(mapped)!;
  return byId.get(name) ?? byId.get(`minecraft:${name}`) ?? null;
}

/**
 * Java name used to draw a catalog item. Bed colours are carried in the extra
 * number (`minecraft:bed:14` → `minecraft:red_bed`); the plain ID is white.
 */
export function catalogItemToBlockName(id: string): string {
  const bed = /^minecraft:bed(?::(\d+))?$/.exec(id);
  if (bed) return `minecraft:${BED_COLORS[Number(bed[1] ?? 0)] ?? 'white'}_bed`;
  return convertBedrockBlock(id.replace(/:\d+$/, ''), {}).name;
}
